import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { refreshTokenWithLock } from '#/api/client'
import type { IUser, TRole } from '#interfaces/auth'
import { hasRole as checkRole, ROLE_ADMIN, ROLE_SUPER_ADMIN, ROLE_USER } from '#interfaces/auth'
import * as authApi from '#lib/authApi'
import { AuthApiError } from '#lib/authApi'
import { postSignedOut, subscribeSignedOut } from '#lib/authChannel'
import { decodeUserFromToken, getRefreshDelay } from '#lib/jwt'
import {
  generateCodeChallenge,
  generateCodeVerifier,
  retrieveOAuthState,
  retrievePkceVerifier,
  storeOAuthState,
  storePkceVerifier,
  storeRedirectPath,
} from '#lib/pkce'
import { clearProfilePromptDismissal } from '#lib/profilePrompt'
import { ensureSession } from '#lib/session'
import { clearToken, getToken, setToken, useToken } from '#lib/tokenStore'

import type { IAuthContext } from './interfaces'

// Fenced behind `import.meta.env.DEV` so the bypass is dead code in production
// builds — a stray `REACT_APP_DEV_AUTH_BYPASS=true` in a prod env can never skip
// OAuth. `import.meta.env.DEV` is true for `vite dev` (incl. `bun dev:mock`) and
// false for `vite build`.
const DEV_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.REACT_APP_DEV_AUTH_BYPASS === 'true'

// Optional dev-only role switch. Unset → the historical admin/super-admin dev
// user (unchanged). Set to `user` → an end-user (ROLE_USER) persona so the
// self-view pages (e.g. /profile) can be previewed locally. Only read when
// DEV_AUTH_BYPASS is on; inert otherwise. See scripts/dev-mock-api.ts.
const DEV_AUTH_ROLE = import.meta.env.REACT_APP_DEV_AUTH_ROLE

const DEV_ADMIN_USER: IUser = {
  id: 'dev-user-001',
  email: 'admin@wijmobiel.nl',
  firstName: 'Ad',
  lastName: 'Min',
  displayName: 'Ad Min',
  roles: [ROLE_ADMIN, ROLE_SUPER_ADMIN],
  locale: 'nl',
  enabled: true,
  locked: false,
}

// End-user persona from the "Persoonlijke omgeving – Profiel" FO mockup.
// companyId/companyName feed UserNavProvider (see routes/_authenticated.tsx),
// which the self-view fetch hooks (useFetchProfile, useUserPhoto) key off.
const DEV_END_USER: IUser = {
  id: 'dev-user-role-user-001',
  email: 'puya.sarmidani@wijmobiel.nl',
  firstName: 'Puya',
  lastName: 'Sarmidani',
  displayName: 'Puya Sarmidani',
  roles: [ROLE_USER],
  locale: 'nl',
  enabled: true,
  locked: false,
  companyId: 'dev-company-001',
  companyName: 'WijMobiel Demo',
}

const DEV_USER: IUser = DEV_AUTH_ROLE === 'user' ? DEV_END_USER : DEV_ADMIN_USER

// Write dev token synchronously at module level so it's available
// BEFORE TanStack Router's beforeLoad guard runs.
// This eliminates the flash-to-login race condition on reload/HMR.
if (DEV_AUTH_BYPASS) setToken('dev-bypass-token')

export const AuthContext = createContext<IAuthContext | null>(null)

interface IProps {
  children: React.ReactNode
}

async function initiateLoginFlow(): Promise<void> {
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  storePkceVerifier(codeVerifier)
  storeRedirectPath(window.location.pathname)

  const response = await authApi.authorize(codeChallenge)
  storeOAuthState(response.state)

  window.location.href = response.auth_url
}

async function handleCallbackFlow(code: string, state: string, onTokenStored: () => void): Promise<IUser> {
  const storedState = retrieveOAuthState()
  if (storedState && storedState !== state) throw new Error('OAuth state mismatch — possible CSRF attack')

  const codeVerifier = retrievePkceVerifier()
  if (!codeVerifier) throw new Error('Missing PKCE code verifier')

  const response = await authApi.callback(code, codeVerifier, state)
  setToken(response.token)
  // Every throw above is before the token exists. Bump only once it is stored,
  // and before the next await, so a failed exchange leaves an in-flight profile
  // load alone and a successful one still blocks that load from clearing this token.
  onTokenStored()

  // /auth/me is the effective company. The callback user is only the fallback,
  // and its company was already taken from selectedCompany when that id exists.
  try {
    return await authApi.me(response.token)
  } catch {
    return response.user
  }
}

export const AuthProvider = ({ children }: IProps): React.JSX.Element => {
  const token = useToken()
  const [user, setUser] = useState<IUser | null>(DEV_AUTH_BYPASS ? DEV_USER : null)
  const [isLoading, setIsLoading] = useState<boolean>(!DEV_AUTH_BYPASS)
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(null)
  const initRef = useRef<boolean>(false)
  // Each profile write takes the next number. Only the latest one may set the
  // user. Sign-in bumps this after the access token is stored, so a callback
  // that fails before then leaves an in-flight load alone.
  const profileLoadEpochRef = useRef(0)

  // On mount: await the once-per-document session bootstrap, then hydrate
  useEffect(() => {
    if (initRef.current) return
    initRef.current = true

    const hydrateFromServer = async (activeToken: string): Promise<void> => {
      const epoch = (profileLoadEpochRef.current += 1)
      const stillCurrent = (): boolean => profileLoadEpochRef.current === epoch

      try {
        const fetchedUser = await authApi.me(activeToken)
        if (!stillCurrent()) return
        setUser(fetchedUser)
      } catch (error) {
        if (!stillCurrent()) return
        if (error instanceof AuthApiError && error.isTokenExpired) {
          try {
            const newToken = await refreshTokenWithLock()
            // The lock does not store the token. Skip it when a newer load,
            // such as the OAuth callback, already replaced this session.
            if (!stillCurrent()) return
            setToken(newToken)
            const fetchedUser = await authApi.me(newToken)
            if (!stillCurrent()) return
            setUser(fetchedUser)
          } catch {
            if (!stillCurrent()) return
            clearToken()
            setUser(null)
          }
        } else {
          clearToken()
          setUser(null)
        }
      }
    }

    const initialize = async (): Promise<void> => {
      if (DEV_AUTH_BYPASS) return

      const activeToken = await ensureSession()
      if (!activeToken) {
        setUser(null)
        setIsLoading(false)
        return
      }

      const jwtUser = decodeUserFromToken(activeToken)
      if (jwtUser) {
        setUser(jwtUser)
        setIsLoading(false)
        // Background: fetch full server-verified user data (isImpersonating, enabled, locked)
        void hydrateFromServer(activeToken)
        return
      }

      // JWT decode failed — fall back to server call
      await hydrateFromServer(activeToken)
      setIsLoading(false)
    }

    initialize()
  }, [])

  useEffect(() => {
    return subscribeSignedOut((): void => {
      clearProfilePromptDismissal()
      setUser(null)
      setImpersonatedEmail(null)
      clearToken()
    })
  }, [])

  // Proactive token refresh: schedule a refresh before the token expires
  useEffect(() => {
    if (DEV_AUTH_BYPASS || !token || !user) return

    let timerId: ReturnType<typeof setTimeout> | null = null
    let isCancelled = false

    const performRefresh = async (): Promise<void> => {
      if (isCancelled) return

      try {
        const newToken = await refreshTokenWithLock()
        // A newer token may already have been stored while this refresh ran.
        if (isCancelled) return

        setToken(newToken)
        // New token triggers this effect to re-run → schedules next refresh
      } catch {
        // Refresh failed — the 401 middleware will handle it on next API call.
        // Don't clear the token here; let the user continue until an API call fails.
      }
    }

    const scheduleRefresh = (): void => {
      if (isCancelled || !token) return

      const delay = getRefreshDelay(token)
      if (delay === null) return

      // Token already expired or about to — refresh immediately
      if (delay <= 0) {
        performRefresh()
        return
      }

      timerId = setTimeout(performRefresh, delay)
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible' || isCancelled) return

      // Tab became visible — check if token needs refresh now
      if (timerId !== null) clearTimeout(timerId)
      scheduleRefresh()
    }

    scheduleRefresh()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return (): void => {
      isCancelled = true
      if (timerId !== null) clearTimeout(timerId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [token, user])

  const handleCallback = useCallback(async (code: string, state: string): Promise<IUser> => {
    let epoch = profileLoadEpochRef.current
    const callbackUser = await handleCallbackFlow(code, state, (): void => {
      epoch = profileLoadEpochRef.current += 1
    })
    // A profile load that started after the token was stored owns the user.
    if (profileLoadEpochRef.current === epoch) setUser(callbackUser)
    return callbackUser
  }, [])

  const handleLogout = useCallback(async (): Promise<void> => {
    clearProfilePromptDismissal()
    const currentToken = token
    setUser(null)
    setImpersonatedEmail(null)
    clearToken()
    postSignedOut()

    if (currentToken) {
      const logoutResponse = await authApi.logout(currentToken)
      if (logoutResponse?.logout_url) {
        window.location.href = logoutResponse.logout_url
        return
      }
    }
  }, [token])

  const hasMinimumRole = useCallback(
    (role: TRole): boolean => {
      return checkRole(user?.roles, role)
    },
    [user],
  )

  const startImpersonation = useCallback(
    async (email: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')

      const epoch = (profileLoadEpochRef.current += 1)
      const impersonatedUser = await authApi.me(token, email)
      if (profileLoadEpochRef.current !== epoch) throw new Error('Profile load is no longer current')
      if (!impersonatedUser.isImpersonating) throw new Error('Impersonation failed — user may not have an account')

      setImpersonatedEmail(email)
      setUser(impersonatedUser)
    },
    [token],
  )

  const stopImpersonation = useCallback(async (): Promise<void> => {
    if (!token) throw new Error('Not authenticated')

    const epoch = (profileLoadEpochRef.current += 1)
    const originalUser = await authApi.me(token)
    if (profileLoadEpochRef.current !== epoch) throw new Error('Profile load is no longer current')

    setImpersonatedEmail(null)
    setUser(originalUser)
  }, [token])

  const refreshUser = useCallback(async (): Promise<IUser> => {
    // The company PATCH can refresh the access token before this runs.
    // Read the store now, and join the shared refresh if that token is expired.
    const activeToken = getToken()
    if (!activeToken) throw new Error('Not authenticated')

    const epoch = (profileLoadEpochRef.current += 1)
    const stillCurrent = (): boolean => profileLoadEpochRef.current === epoch

    const loadProfile = async (profileToken: string): Promise<IUser> => {
      const freshUser = await authApi.me(profileToken, impersonatedEmail ?? undefined)
      if (!stillCurrent()) throw new Error('Profile load is no longer current')
      setUser(freshUser)
      return freshUser
    }

    try {
      return await loadProfile(activeToken)
    } catch (error) {
      if (!stillCurrent()) throw error
      if (!(error instanceof AuthApiError) || !error.isTokenExpired) throw error

      const newToken = await refreshTokenWithLock()
      if (!stillCurrent()) throw new Error('Profile load is no longer current')
      setToken(newToken)
      return loadProfile(newToken)
    }
  }, [impersonatedEmail])

  const contextValue = useMemo(
    (): IAuthContext => ({
      user,
      isAuthenticated: !!token && !!user,
      isLoading,
      isImpersonating: impersonatedEmail !== null,
      initiateLogin: initiateLoginFlow,
      handleCallback,
      logout: handleLogout,
      hasMinimumRole,
      startImpersonation,
      stopImpersonation,
      refreshUser,
    }),
    [
      user,
      token,
      isLoading,
      impersonatedEmail,
      handleCallback,
      handleLogout,
      hasMinimumRole,
      startImpersonation,
      stopImpersonation,
      refreshUser,
    ],
  )

  return <AuthContext value={contextValue}>{children}</AuthContext>
}
